import React, { useState } from 'react';
import { Box, SelectChangeEvent } from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  CustomChip,
  CustomInput,
  CustomSelect,
  IconBtn,
  StyledChipContainer,
  StyledEditSection,
  StyledKeywordLabel,
  StyledKeywordValue,
  StyledMenuItem,
  StyledViewSection,
  StyledErrorText,
} from './Style/CustomEditableChips.style';
import { DOMAIN_REGEX } from '../../utils';
import {
  KeywordWeightOption,
  KeywordWeightTooltip,
  KeywordWeightValue,
} from '../KeywordWeight/KeywordWeight';

export type ChipData = {
  label: string;
  value?: number;
};

type EditableChipProps = {
  chipData: ChipData;
  isEditing: boolean;
  onRequestEdit: () => void;
  onCancel: () => void;
  onUpdate: (updatedChip: ChipData) => void;
  onDelete: () => void;
  selectOptions: number[];
  readOnly?: boolean;
};

const EditableChip: React.FC<EditableChipProps> = ({
  chipData,
  isEditing,
  onRequestEdit,
  onCancel,
  onUpdate,
  onDelete,
  selectOptions,
  readOnly = false,
}) => {
  const [editedChipData, setEditedChipData] = useState(chipData);
  const [isHovered, setIsHovered] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSave = () => {
    if (!editedChipData.label.trim()) {
      setError('Invalid Keyword');
    } else if (!DOMAIN_REGEX.test(editedChipData.label)) {
      setError('Invalid Keyword');
    } else {
      setError(null);
      onUpdate(editedChipData);
    }
  };

  const handleSelectChange = (e: SelectChangeEvent<unknown>) => {
    const newValue = e.target.value as string;
    setEditedChipData({
      ...editedChipData,
      value: Number(newValue),
    });
  };

  const handleCancel = () => {
    onCancel();
    setError(null);
  };

  const handleBlur = () => {
    if (!editedChipData.label.trim()) {
      setError('Invalid Keyword');
    } else if (!DOMAIN_REGEX.test(editedChipData.label)) {
      setError('Invalid Keyword');
    } else {
      setError(null);
    }
  };

  return (
    <Box>
      <StyledChipContainer readOnly={readOnly}>
        {isEditing && !readOnly ? (
          <StyledEditSection>
            <CustomInput
              value={editedChipData.label}
              onChange={e =>
                setEditedChipData({ ...editedChipData, label: e.target.value })
              }
              onBlur={handleBlur}
              placeholder="Keyword"
              error={!!error}
            />
            {editedChipData?.value !== undefined && (
              <CustomSelect
                size="small"
                variant="standard"
                value={editedChipData?.value?.toString() || ''}
                onChange={handleSelectChange}
                renderValue={v => (
                  <KeywordWeightValue weight={Number(v)} compact />
                )}
                data-testid="keywords-chip-weight-select"
                MenuProps={{ PaperProps: { sx: { maxWidth: 420 } } }}
              >
                {selectOptions?.map(option => (
                  <StyledMenuItem
                    key={option}
                    value={option?.toString()}
                    data-testid={`keywords-chip-weight-option-${option}`}
                  >
                    <KeywordWeightOption weight={option} />
                  </StyledMenuItem>
                ))}
              </CustomSelect>
            )}
            <IconBtn onClick={handleSave} disabled={!!error}>
              <CheckIcon fontSize="small" />
            </IconBtn>
            <IconBtn onClick={handleCancel}>
              <CloseIcon fontSize="small" />
            </IconBtn>
          </StyledEditSection>
        ) : (
          <StyledViewSection
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            <CustomChip
              label={
                <>
                  <StyledKeywordLabel readOnly={readOnly}>
                    {chipData?.label}{' '}
                  </StyledKeywordLabel>
                  {chipData?.value !== undefined && (
                    <KeywordWeightTooltip weight={chipData.value}>
                      <StyledKeywordValue data-testid="keywords-chip-weight">
                        {chipData.value}
                      </StyledKeywordValue>
                    </KeywordWeightTooltip>
                  )}
                </>
              }
            />
            {!readOnly && isHovered && (
              <>
                <IconBtn onClick={onRequestEdit}>
                  <EditIcon fontSize="small" />
                </IconBtn>
                <IconBtn onClick={onDelete}>
                  <DeleteIcon fontSize="small" />
                </IconBtn>
              </>
            )}
          </StyledViewSection>
        )}
      </StyledChipContainer>
      {error && <StyledErrorText>{error}</StyledErrorText>}
    </Box>
  );
};

export default EditableChip;
